// ─── Countdown: Luxe ──────────────────────────────────────────
// 3-column countdown in a bordered panel. Dark-theme friendly.
// Shows days, hours, minutes with elegant card styling.

import React, { useState, useEffect } from 'react';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';

function useCountdown(targetDate) {
  const [tl, setTl] = useState({ days: 0, hours: 0, minutes: 0 });
  useEffect(() => {
    if (!targetDate) return;
    const tick = () => {
      const diff = Math.max(0, new Date(targetDate).getTime() - Date.now());
      setTl({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
      });
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [targetDate]);
  return tl;
}

export default function CountdownLuxe({ section, data, theme }) {
  const anim = section.animation || {};
  const countdown = useCountdown(data.targetDateTime);
  if (!data.targetDateTime) return null;

  return (
    <section className="px-6 py-20 sm:py-24" data-testid="countdown-section">
      <AnimatedSection preset={anim.section || 'fade-in'}>
        <div
          className="mx-auto max-w-5xl rounded-[2rem] border px-6 py-10 sm:px-10"
          style={{ backgroundColor: theme.surface, borderColor: theme.border }}
        >
          <div className="text-center mb-10">
            <p
              className="text-[11px] uppercase tracking-[0.2em] mb-3"
              style={{ color: theme.accent, fontFamily: theme.eyebrowFont }}
            >
              Countdown
            </p>
            <h2
              className="text-3xl sm:text-4xl"
              style={{ fontFamily: theme.headingFont, color: theme.textPrimary }}
            >
              {data.label || 'Until the celebration begins'}
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto">
            {[
              { label: 'Days', value: countdown.days },
              { label: 'Hours', value: countdown.hours },
              { label: 'Minutes', value: countdown.minutes },
            ].map(item => (
              <div
                key={item.label}
                className="rounded-[1.6rem] border px-4 py-6 text-center"
                style={{ borderColor: theme.border, backgroundColor: theme.bg }}
              >
                <div
                  className="text-3xl sm:text-5xl tabular-nums"
                  style={{ fontFamily: theme.headingFont, color: theme.textPrimary }}
                >
                  {String(item.value).padStart(2, '0')}
                </div>
                <div
                  className="mt-2 text-[11px] uppercase tracking-[0.18em]"
                  style={{ color: theme.textSecondary, fontFamily: theme.eyebrowFont }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>
    </section>
  );
}
